import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import {
  formatTimeInUserTimezone,
  formatDateInUserTimezone,
  getUserTimezone,
} from "./utils/timezoneHelper";

export default function Notifications() {
  const navigate = useNavigate();
  const [showMealModal, setShowMealModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userTimezone = getUserTimezone();
  const [notificationsRecent, setNotificationsRecent] = useState([]);
  const [notificationsOlder, setNotificationsOlder] = useState([]);
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  // Icon map based on reminder types
  const iconMap = {
    obat: "/icon-obat.png",
    makan: "/icon-makan.png",
    kontrol: "/icon-dokter.png",
    terapi: "/icon-terapi.png",
  };

  // Type labels for display
  const typeLabels = {
    obat: "Minum obat",
    makan: "Makan",
    kontrol: "Konsultasi dokter",
    terapi: "Terapi",
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    
    const userDate = new Date(date.toLocaleString("en-US", { timeZone: userTimezone }));
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: userTimezone }));
    
    const diffInMinutes = Math.floor((now - userDate) / (1000 * 60));
  
    if (diffInMinutes < 1) {
      return "Baru saja";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} menit yang lalu`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} jam yang lalu`;
    } else if (diffInMinutes < 10080) {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} hari yang lalu`;
    } else if (diffInMinutes < 43200) {
      const weeks = Math.floor(diffInMinutes / 10080);
      return `${weeks} minggu yang lalu`;
    } else {
      const months = Math.floor(diffInMinutes / 43200);
      return `${months} bulan yang lalu`;
    }
  };

  const categorizeNotifications = (reminders) => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: userTimezone }));
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  
    const recent = [];
    const older = [];
  
    reminders.forEach((reminder) => {
      // Use UpdatedAt instead of waktu for notification timing
      const notificationDate = new Date(new Date(reminder.UpdatedAt || reminder.updated_at).toLocaleString("en-US", { timeZone: userTimezone }));
  
      // Convert reminder to notification format
      const notification = {
        id: reminder.ID || reminder.id, // Use capital ID first
        type: reminder.tipe,
        title: reminder.judul,
        time: formatTime(reminder.UpdatedAt || reminder.updated_at), // Use UpdatedAt for "time ago"
        originalData: reminder,
        isDone: reminder.is_done,
        details: reminder.details,
        displayTime: formatTimeInUserTimezone(reminder.waktu, userTimezone), // Keep waktu for scheduled time
        displayDate: formatDateInUserTimezone(reminder.waktu, userTimezone), // Keep waktu for scheduled date
      };
  
      if (notificationDate >= sevenDaysAgo) {
        recent.push(notification);
      } else if (notificationDate >= thirtyDaysAgo) {
        older.push(notification);
      }
    });
  
    // Sort by UpdatedAt (most recent first)
    recent.sort((a, b) => {
      const dateA = new Date(a.originalData.UpdatedAt || a.originalData.updated_at);
      const dateB = new Date(b.originalData.UpdatedAt || b.originalData.updated_at);
      return dateB - dateA;
    });
    
    older.sort((a, b) => {
      const dateA = new Date(a.originalData.UpdatedAt || a.originalData.updated_at);
      const dateB = new Date(b.originalData.UpdatedAt || b.originalData.updated_at);
      return dateB - dateA;
    });
  
    return { recent, older };
  };
  
 

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all reminders
        const response = await api.get("/pengingat");
        const reminders = response.data;

        // Categorize notifications
        const { recent, older } = categorizeNotifications(reminders);

        setNotificationsRecent(recent);
        setNotificationsOlder(older);
      } catch (err) {
        console.error("Failed to fetch reminders:", err);
        setError("Gagal memuat notifikasi");

        // Fallback to empty arrays
        setNotificationsRecent([]);
        setNotificationsOlder([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
    if (!userLoading) {
      logUserActivity(
        "user",
        "view_notifications",
        "Notifications",
        "User viewed notifications"
      );
    }
  }, [userLoading]);

  const handleNotificationClick = (notification) => {
    // Handle different types of notifications
    switch (notification.type) {
      case "makan":
        setSelectedReminder(notification);
        setShowMealModal(true);
        break;
      case "obat":
        setSelectedReminder(notification);
        setShowMealModal(true); // Reuse modal for medicine reminders
        break;
      case "kontrol":
        // Could navigate to appointment details
        setSelectedReminder(notification);
        setShowMealModal(true); // Reuse modal for medicine reminders

        console.log("Kontrol reminder clicked:", notification);
        break;
      case "terapi":
        // Could navigate to therapy details
        setSelectedReminder(notification);
        setShowMealModal(true); // Reuse modal for medicine reminders

        console.log("Terapi reminder clicked:", notification);
        break;
      default:
        console.log("Unknown notification type:", notification);
    }
  };

  const handleReminderComplete = async (isComplete) => {
    if (!selectedReminder) return;

    try {
      // Debug log untuk melihat struktur data lengkap
      // console.log("Selected reminder full data:", selectedReminder);
      // console.log("Original data:", selectedReminder.originalData);

      // Cari ID dari berbagai kemungkinan lokasi
      let reminderId =
        selectedReminder.ID ||
        selectedReminder.id ||
        selectedReminder.originalData?.ID ||
        selectedReminder.originalData?.id;

      // Jika masih undefined, coba dari field lain
      if (!reminderId) {
        console.error("Cannot find reminder ID in:", selectedReminder);
        alert("ID pengingat tidak ditemukan. Silakan refresh halaman.");
        return;
      }

      console.log("Using reminder ID:", reminderId);

      const requestBody = {
        is_done: isComplete,
      };

      // console.log("Making request:", {
      //   url: `/pengingat/${reminderId}`,
      //   body: requestBody,
      // });

      await api.put(`/pengingat/${reminderId}`, requestBody);

      // Update local state
      const updateNotifications = (notifications) =>
        notifications.map((notif) => {
          const notifId =
            notif.ID ||
            notif.id ||
            notif.originalData?.ID ||
            notif.originalData?.id;

          return notifId === reminderId
            ? { ...notif, isDone: isComplete, is_done: isComplete }
            : notif;
        });

      setNotificationsRecent((prev) => updateNotifications(prev));
      setNotificationsOlder((prev) => updateNotifications(prev));

      setShowMealModal(false);
      setSelectedReminder(null);

      logUserActivity(
        "user",
        "update_reminder_status",
        "Notifications",
        `User marked reminder as ${isComplete ? "completed" : "not completed"}: ${selectedReminder.title || selectedReminder.judul}`
      );

      console.log("Reminder status updated successfully");
    } catch (err) {
      console.error("Failed to update reminder:", err);
      console.error("Error details:", {
        status: err.response?.status,
        data: err.response?.data,
        selectedReminder: selectedReminder,
      });

      let errorMessage = "Gagal memperbarui status pengingat";

      if (err.response?.status === 400) {
        errorMessage = "Data tidak valid. Periksa kembali pengingat.";
      } else if (err.response?.status === 404) {
        errorMessage = "Pengingat tidak ditemukan.";
      } else if (err.response?.status >= 500) {
        errorMessage = "Server bermasalah. Coba lagi nanti.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat notifikasi...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_notifications_error",
                "Notifications",
                "User clicked back from notifications error"
              );
              navigate("/");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Notifikasi</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-4 pb-24 relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_notifications",
              "Notifications",
              "User clicked back from notifications"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Notifikasi</h1>
        <div className="h-6 w-6" />
      </div>

      {/* Recent notifications */}
      <p className="text-md font-semibold text-black mb-2">7 hari terakhir</p>
      {notificationsRecent.length > 0 ? (
        <div className="space-y-4 mb-6">
          {notificationsRecent.map((item) => (
            <div
              key={item.id}
              onClick={() => handleNotificationClick(item)}
              className={`flex items-center justify-between border-b pb-2 cursor-pointer hover:bg-gray-50 transition ${
                item.isDone ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden relative">
                  <img
                    src={iconMap[item.type] || "/icon-default.png"}
                    alt={typeLabels[item.type] || item.type}
                    className="object-contain w-6 h-6"
                  />
                  {item.isDone && (
                    <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-blue-600">
                    {typeLabels[item.type] || item.type}
                  </p>
                  <p
                    className={`text-sm font-semibold ${item.isDone ? "line-through" : ""}`}
                  >
                    {item.title}
                  </p>
                  {item.details && item.type === "obat" && (
                    <p className="text-xs text-gray-500">
                      {item.details.nama_obat} - {item.details.frekuensi}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 whitespace-nowrap">
                {item.time}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 mb-6">
          <p className="text-gray-500">
            Tidak ada notifikasi dalam 7 hari terakhir
          </p>
        </div>
      )}

      {/* Older notifications */}
      <p className="text-md font-semibold text-black mb-2">30 hari terakhir</p>
      {notificationsOlder.length > 0 ? (
        <div className="space-y-4">
          {notificationsOlder.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between border-b pb-2 ${
                item.isDone ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden relative">
                  <img
                    src={iconMap[item.type] || "/icon-default.png"}
                    alt={typeLabels[item.type] || item.type}
                    className="object-contain w-6 h-6"
                  />
                  {item.isDone && (
                    <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-blue-600">
                    {typeLabels[item.type] || item.type}
                  </p>
                  <p
                    className={`text-sm font-semibold ${item.isDone ? "line-through" : ""}`}
                  >
                    {item.title}
                  </p>
                  {item.details && item.type === "obat" && (
                    <p className="text-xs text-gray-500">
                      {item.details.nama_obat} - {item.details.frekuensi}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 whitespace-nowrap">
                {item.time}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">
            Tidak ada notifikasi dalam 30 hari terakhir
          </p>
        </div>
      )}

      {showMealModal && selectedReminder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full text-center mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {(() => {
                switch (selectedReminder.type) {
                  case "obat":
                    return "Sudah minum obat?";
                  case "makan":
                    return "Sudah makan?";
                  case "kontrol":
                    return "Sudah konsultasi dokter?";
                  case "terapi":
                    return "Sudah terapi?";
                  default:
                    return "Sudah selesai?";
                }
              })()}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {selectedReminder.title}
            </p>

            {selectedReminder.details && selectedReminder.type === "obat" && (
              <div className="text-left bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-xs text-gray-600">Detail:</p>
                <p className="text-sm font-medium">
                  {selectedReminder.details.nama_obat}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedReminder.details.frekuensi}
                </p>
                {selectedReminder.details.aturan && (
                  <p className="text-xs text-gray-500">
                    {selectedReminder.details.aturan}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-around text-sm font-medium">
              <button
                onClick={() => handleReminderComplete(false)}
                className="border border-red-500 text-red-500 px-4 py-2 rounded-lg flex items-center gap-1 hover:bg-red-50 transition"
              >
                ❌ Belum
              </button>
              <button
                onClick={() => handleReminderComplete(true)}
                className="border border-green-500 text-green-500 px-4 py-2 rounded-lg flex items-center gap-1 hover:bg-green-50 transition"
              >
                ✅ Sudah
              </button>
            </div>

            <button
              onClick={() => {
                setShowMealModal(false);
                setSelectedReminder(null);
              }}
              className="mt-3 text-sm text-gray-500 hover:text-gray-700"
            >
              Tutup
            </button>
          </div>
        </div>
      )}
      {/* setShowMealModal(false); */}
      {/* setSelectedReminder(null); */}
      {/* }} */}
      {/* className="mt-3 text-sm text-gray-500 hover:text-gray-700" */}
      {/* > */}
      {/* Tutup */}
      {/* </button> */}
      {/* // </div> */}
      {/* // </div> */}
      {/* )} */}
    </div>
  );
}
